import React, { useEffect } from "react";
import styled from "styled-components";
import { animateScroll as scroll } from "react-scroll";
import Testimonial from "../components/Sections/Testimonial";
import DemoCredentialButton from "../components/Elements/DemoCredentialButton";
import { APP_CREDENTIALS } from "../config/demoCredentials";
import {
  MdOutlineMedicalServices,
  MdOutlineMonitorHeart,
  MdOutlineAnalytics,
  MdOutlineSpeed,
} from "react-icons/md";
import { FaGooglePlay, FaApple } from "react-icons/fa";

const UlcerSightPage = () => {
  useEffect(() => {
    scroll.scrollToTop();
  }, []);

  const handleTryDemo = () => {
    window.open(
      "https://demo.conversantech.com/ulcer-sight",
      "_blank"
    );
  };

  const features = [
    {
      icon: <MdOutlineMedicalServices fontSize={26} />,
      title: "AI-Powered Diagnostics",
      description:
        "Utilize advanced machine learning models to detect and analyze ulcers with high accuracy.",
    },
    {
      icon: <MdOutlineSpeed fontSize={26} />,
      title: "Rapid Results",
      description:
        "Get instant feedback and analysis reports to accelerate medical decision-making.",
    },
    {
      icon: <MdOutlineMonitorHeart fontSize={26} />,
      title: "Patient Monitoring",
      description:
        "Keep track of patient progress and ulcer healing over time with visual analytics.",
    },
    {
      icon: <MdOutlineAnalytics fontSize={26} />,
      title: "Comprehensive Analytics",
      description:
        "Access detailed reports and analytics to improve treatment strategies and outcomes.",
    }
  ];

  return (
    <>
      <Wrapper className="flexNullCenter flexColumn">
        <HeroContainer>
          <ContentWrapper>
            <TextContent>
              <Title>Ulcer Sight</Title>
              <Description>
                Ulcer Sight is an advanced AI-powered medical application designed specifically for the detection and analysis of ulcers. It uses cutting-edge machine learning models to analyze visual inputs and provide accurate diagnostic assistance in real-time. Simply capture an image of the affected area using your device, and the app will instantly process it to identify the presence and severity of ulcers. The tool provides comprehensive analytics, helps track patient healing progress over time, and delivers rapid results to accelerate medical decision-making for healthcare professionals and patients alike.
              </Description>
              <ButtonWrapper>
                <DemoCredentialButton credentials={APP_CREDENTIALS.ulcerSight} />
                <TryButton onClick={handleTryDemo}>Try it</TryButton>
                <StoreButton onClick={() => window.open("https://play.google.com/store", "_blank")}>
                  <FaGooglePlay style={{ marginRight: '8px' }} /> Google Play
                </StoreButton>
                <StoreButton onClick={() => window.open("https://www.apple.com/app-store/", "_blank")}>
                  <FaApple style={{ marginRight: '8px', fontSize: '18px' }} /> App Store
                </StoreButton>
              </ButtonWrapper>
            </TextContent>
          </ContentWrapper>
        </HeroContainer>
      </Wrapper>

      <DemoSampleWrapper className="lightBg">
        <div className="container">
          <HeaderInfo>
            <h1 className="font40 extraBold">Features</h1>
            <p className="font13">
              Discover powerful features designed to help you analyze and monitor ulcers efficiently.
            </p>
          </HeaderInfo>
          <DemoSampleWrapperDiv>
            {features.map((feature, index) => (
              <FeatureWrapper
                key={index}
                className="whiteBg radius8 shadow"
              >
                <DemoFeatureDiv>
                  <DemoFeatureIcon>{feature.icon}</DemoFeatureIcon>
                  <DemoFeatureTitle className="font20 extraBold">
                    {feature.title}
                  </DemoFeatureTitle>
                </DemoFeatureDiv>
                <DemoFeatureDesc className="font13">
                  {feature.description}
                </DemoFeatureDesc>
              </FeatureWrapper>
            ))}
          </DemoSampleWrapperDiv>
        </div>
      </DemoSampleWrapper>

      {/* Testimonial */}
      <Testimonial />
    </>
  );
};

const Wrapper = styled.section`
  width: 100%;
  max-width: 1220px;
  margin: 0 auto;
  padding: 110px 30px 0px 30px;
  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

const HeroContainer = styled.section`
  background-color: #f9fafb;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 32px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 32px;

  @media (max-width: 1024px) {
    flex-direction: column;
    text-align: center;
  }
`;

const TextContent = styled.div`
  flex: 1;
  min-width: 300px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
`;

const Description = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin-bottom: 32px;
  line-height: 2;
`;

const DemoSampleWrapper = styled.div`
  margin-top: 50px;
  padding: 50px 0px;
`;

const HeaderInfo = styled.div`
  margin-bottom: 30px;
  @media (max-width: 860px) {
    text-align: center;
  }
`;

const DemoSampleWrapperDiv = styled.div`
  width: 100%;
  max-width: 1220px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const FeatureWrapper = styled.div`
  width: 350px;
  height: 220px;
  text-align: left;
  padding: 20px 30px;
  margin: 30px 0px;
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: scale(1.05) translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  }
`;

const DemoFeatureDiv = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const DemoFeatureIcon = styled.div`
  width: 30%;
  width: 60px;
  height: 60px;
  background-color: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
`;

const DemoFeatureTitle = styled.h3`
  width: 70%;
  padding: 10px 15px;
`;

const DemoFeatureDesc = styled.p`
  padding: 10px 0px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

const TryButton = styled.button`
  background-color: #7620ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
  box-shadow: 0 4px 6px rgba(118, 32, 255, 0.2);

  &:hover {
    background-color: #6010e2;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const StoreButton = styled(TryButton)`
  background-color: #111;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: #000;
  }
`;

export default UlcerSightPage;
